import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { GroupId, ViewModelId } from 'balus-common';
import { generateDefaultUserIcon } from '@framework/utils';
import { formatFileSize } from '@framework/utils/formatFileSize';
import { formatRelativeTime } from '@framework/i18n/formatRelativeTime';
import type { RelativeTimeI18nKeys } from '@framework/i18n/formatRelativeTime';
import { Timestamp } from '@framework/Timestamp';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { fileExtensionIcon } from '@view-model/models/sticky/FileContainer/components/fileExtensionIcon';
import type { FileContainerListItem } from './useFetchFileContainers';

const RELATIVE_TIME_I18N_KEYS = {
  minutes: 'viewModel.fileList.relativeTime.minutes',
  hours: 'viewModel.fileList.relativeTime.hours',
  days: 'viewModel.fileList.relativeTime.days',
  weeks: 'viewModel.fileList.relativeTime.weeks',
  months: 'viewModel.fileList.relativeTime.months',
  years: 'viewModel.fileList.relativeTime.years',
} as const satisfies RelativeTimeI18nKeys;

type Props = {
  item: FileContainerListItem;
  groupId: GroupId;
  viewModelId: ViewModelId;
};

export const FileListItem: React.FC<Props> = ({ item: { container, view }, groupId, viewModelId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const author = useUserPublicProfile(container.createdUserId);

  const handleMoveToFileContainer = useCallback(() => {
    const query = new URLSearchParams();
    query.set('moveTime', Date.now().toString());
    query.set('targetFileContainer', container.id);
    query.set('targetModel', view.modelId);
    query.set('targetView', view.id);
    navigate({ search: query.toString() }, { replace: true });
  }, [container.id, view, navigate]);

  return (
    <button
      type="button"
      className="block w-full cursor-pointer px-4 py-3 text-left hover:bg-gray-50"
      onClick={handleMoveToFileContainer}
    >
      <div className="mb-1.5 text-xs text-gray-600">
        {formatRelativeTime(container.createdAt, Timestamp.now(), t, RELATIVE_TIME_I18N_KEYS)}
      </div>
      {author && (
        <div className="flex items-center gap-2">
          <div className="size-6 shrink-0 overflow-hidden rounded-full">
            {author.imageUrl ? (
              <img src={author.imageUrl} alt={author.name} className="size-full bg-gray-400 object-cover" />
            ) : (
              <div className="size-full bg-gray-400">
                {generateDefaultUserIcon({
                  id: author.id,
                  name: author.name,
                  isAnonymous: author.isAnonymous,
                  size: 24,
                })}
              </div>
            )}
          </div>
          <span className="flex-1 truncate text-sm font-bold">{author.name}</span>
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-800">
        <div className="flex size-6 shrink-0 items-center justify-center">
          {container.isImage() ? (
            <img
              src={container.assetUrl(groupId, viewModelId, window.location.origin)}
              alt=""
              loading="lazy"
              className="size-6 rounded object-cover"
            />
          ) : (
            <FontAwesomeIcon icon={fileExtensionIcon(container.filename)} className="text-lg text-gray-600" />
          )}
        </div>
        <span className="flex-1 truncate" title={container.filename}>
          {container.filename}
        </span>
        <span className="shrink-0 text-xs text-gray-600">{formatFileSize(container.fileSize)}</span>
      </div>
    </button>
  );
};
